import { type ChangeEvent, memo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Input } from '@air/primitive-input';

import { type BatchExportImagesFormInput } from '~/components/ExportImagesModal/BatchExportImagesModal';

export type BatchExportSizeInputGroupProps = {
  formId: string;
  hasError: boolean;
  errorId: string;
};

export const BatchExportSizeInputGroup = memo(({ formId, hasError, errorId }: BatchExportSizeInputGroupProps) => {
  const { control, setValue } = useFormContext<BatchExportImagesFormInput>();
  const targetWidth = useWatch({ control, name: 'targetWidth' });
  const targetHeight = useWatch({ control, name: 'targetHeight' });

  const handleTargetWidthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue('targetWidth', event.target.value === '' ? undefined : Number(event.target.value), {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const handleTargetHeightChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue('targetHeight', event.target.value === '' ? undefined : Number(event.target.value), {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  return (
    <div className="flex flex-row items-center gap-2">
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 w-[14px] -translate-y-1/2 text-right ads:text-placeholder text-14 text-gray-7">
          W
        </span>
        <Input
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError}
          autoComplete="off"
          className="w-28 pl-8"
          data-testid="BATCH_EXPORT_TARGET_WIDTH_INPUT"
          hasError={hasError}
          id={`${formId}-targetWidth`}
          name="targetWidth"
          onChange={handleTargetWidthChange}
          type="number"
          value={targetWidth ?? ''}
        />
      </div>
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 w-[14px] -translate-y-1/2 text-right ads:text-placeholder text-14 text-gray-7">
          H
        </span>
        <Input
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError}
          autoComplete="off"
          className="w-28 pl-8"
          data-testid="BATCH_EXPORT_TARGET_HEIGHT_INPUT"
          hasError={hasError}
          id={`${formId}-targetHeight`}
          name="targetHeight"
          onChange={handleTargetHeightChange}
          type="number"
          value={targetHeight ?? ''}
        />
      </div>
    </div>
  );
});

BatchExportSizeInputGroup.displayName = 'BatchExportSizeInputGroup';
